// useNoteGeneralStore.js
// Store Pinia para pesquisa geral de notas.

import { defineStore } from "pinia";
import { ref, reactive, computed } from "vue";
import api from "@/services/api";

export const useNoteGeneralStore = defineStore("noteGeneral", () => {
  // ─────────────────────────────────────────────────────────────────────────
  // ESTADO — filtros
  // ─────────────────────────────────────────────────────────────────────────
  const filters = reactive({
    noteTitle: "",
    noteBody: "",
    activityTitle: "",
    textTitle: "",
    tag: "",
    source: "",
    permissionGroupName: "",
    permissionUserName: "",
    createdFrom: "",
    createdTo: "",
    creatorId: "",
    creatorName: "",
    onlyWithBody: false,
    onlyWithObservation: false,
    onlyDetachedNotes: false,
    // ✅ NOVO: filtro por tipo de observação. Acertos/Erros são mutuamente
    // exclusivos na UI, então há no máximo 1 item. Ex.: ["Correct"].
    // Vazio = sem restrição por tipo.
    observationTypes: [],
    // ✅ NOVO: busca textual no campo observação (combina em AND com o tipo)
    observationSearch: "",
    // ✅ NOVO: estado de leitura da observação (quem marca como lida é o CRIADOR
    // da nota). "" = sem filtro | "read" = lidas | "unread" = não lidas.
    // Independente de observationTypes — combina em AND.
    observationReadState: "",
    // ✅ NOVO: filtra apenas notas criadas pelo caller (espelha onlyMine do ActivityGeneral)
    onlyMine: false,
  });

  // ─────────────────────────────────────────────────────────────────────────
  // ESTADO — paginação e resultados
  // ─────────────────────────────────────────────────────────────────────────
  const notes = ref([]);
  const totalCount = ref(0);
  const totalPages = ref(0);
  const isLoading = ref(false);

  // ─────────────────────────────────────────────────────────────────────────
  // ESTADO — seleção múltipla
  // ─────────────────────────────────────────────────────────────────────────
  const selectedNoteIds = ref([]);

  // ─────────────────────────────────────────────────────────────────────────
  // COMPUTED
  // ─────────────────────────────────────────────────────────────────────────
  const filteredNotes = computed(() => notes.value);

  // ─────────────────────────────────────────────────────────────────────────
  // AÇÕES — filtros
  // ─────────────────────────────────────────────────────────────────────────

  function setFilter(field, value) {
    if (Object.prototype.hasOwnProperty.call(filters, field)) {
      filters[field] = value ?? "";
    }
  }

  function clearFilters() {
    Object.keys(filters).forEach((k) => {
      if (typeof filters[k] === "boolean") filters[k] = false;
      else if (Array.isArray(filters[k])) filters[k] = [];
      else filters[k] = "";
    });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // AÇÕES — seleção
  // ─────────────────────────────────────────────────────────────────────────

  function toggleSelection(noteId) {
    const idx = selectedNoteIds.value.indexOf(noteId);
    if (idx === -1) {
      selectedNoteIds.value.push(noteId);
    } else {
      selectedNoteIds.value.splice(idx, 1);
    }
  }

  function clearSelection() {
    selectedNoteIds.value = [];
  }

  // ─────────────────────────────────────────────────────────────────────────
  // AÇÕES — fetch paginado
  // ─────────────────────────────────────────────────────────────────────────

  async function fetchNotesGeneralPaged(page = 1, pageSize = 12) {
    isLoading.value = true;

    try {
      const params = {
        page,
        pageSize,
      };

      if (filters.noteTitle) params.noteTitle = filters.noteTitle;
      if (filters.noteBody) params.noteBody = filters.noteBody;
      if (filters.activityTitle) params.activityTitle = filters.activityTitle;
      if (filters.textTitle) params.textTitle = filters.textTitle;
      if (filters.tag) params.tag = filters.tag;
      if (filters.source) params.source = filters.source;

      if (filters.permissionGroupName)
        params.permissionGroupName = filters.permissionGroupName;
      if (filters.permissionUserName)
        params.permissionUserName = filters.permissionUserName;

      if (filters.createdFrom) params.createdFrom = filters.createdFrom;
      if (filters.createdTo) params.createdTo = filters.createdTo;
      if (filters.creatorId) params.creatorId = Number(filters.creatorId);
      if (filters.creatorName) params.creatorName = filters.creatorName;

      if (filters.onlyWithBody) params.onlyWithBody = true;
      if (filters.onlyWithObservation) params.onlyWithObservation = true;
      if (filters.onlyDetachedNotes) params.onlyDetachedNotes = true;

      // ✅ NOVO: tipos de observação — envia sempre que houver 1+ selecionado.
      // O backend filtra só por tipo IN (Certo/Errado valem mesmo sem descrição).
      if (
        Array.isArray(filters.observationTypes) &&
        filters.observationTypes.length > 0
      ) {
        params.observationTypes = filters.observationTypes;
      }

      // ✅ NOVO: busca textual na observação
      if (filters.observationSearch && filters.observationSearch.trim() !== "")
        params.observationSearch = filters.observationSearch.trim();

      // ✅ NOVO: estado de leitura — só vai quando definido ("read"/"unread")
      if (filters.observationReadState)
        params.observationReadState = filters.observationReadState;

      // ✅ NOVO: envia onlyMine ao backend quando ativo
      if (filters.onlyMine) params.onlyMine = true;

      const response = await api.get("/NoteGeneral", { params });
      const data = response.data;

      notes.value = Array.isArray(data.items) ? data.items : [];
      totalCount.value = data.totalCount ?? 0;
      totalPages.value = data.totalPages ?? 0;
    } catch (error) {
      console.error(
        "❌ useNoteGeneralStore.fetchNotesGeneralPaged:",
        error?.message,
      );
      throw error;
    } finally {
      isLoading.value = false;
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // EXPOSE
  // ─────────────────────────────────────────────────────────────────────────
  return {
    filters,
    notes,
    totalCount,
    totalPages,
    isLoading,
    selectedNoteIds,
    filteredNotes,
    setFilter,
    clearFilters,
    toggleSelection,
    clearSelection,
    fetchNotesGeneralPaged,
  };
});
